import { sourcingAPI } from '@/utils/api';
import {
  resolveBatchCollectProgress,
  resolveEffectiveCaptureStatus,
  shouldPollSourcingBatch,
} from '@/pages/ipe/workbench/utils/sourcingImageSyncProgress';
import { countListingBatchSpus } from '@/pages/ipe/workbench/utils/listingBatchGroups';

const STORAGE_KEY = 'ipe:pendingListingIngestBatches';
const META_STORAGE_KEY = 'ipe:listingBatchIngestMeta';

function readPendingMap() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const migrated = {};
      parsed.forEach((key) => {
        const bk = String(key || '').trim();
        if (bk) migrated[bk] = { mode: 'ingest', done: 0, total: 0, sourceLabel: '快捷入库' };
      });
      return migrated;
    }
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writePendingMap(map) {
  const entries = Object.entries(map || {}).filter(([key]) => String(key || '').trim());
  try {
    if (entries.length === 0) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* ignore */
  }
}

function readIngestMetaMap() {
  try {
    const raw = localStorage.getItem(META_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeIngestMetaMap(map) {
  const entries = Object.entries(map || {}).filter(([key]) => String(key || '').trim());
  try {
    if (entries.length === 0) localStorage.removeItem(META_STORAGE_KEY);
    else localStorage.setItem(META_STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* ignore */
  }
}

/** 批次预期 SPU 数持久化：session 占位清除后列表仍能显示 x/total */
export function rememberListingBatchIngestMeta(batchKey, meta = {}) {
  const bk = String(batchKey || '').trim();
  if (!bk || isProvisionalListingIngestBatchKey(bk)) return;
  const total = Math.max(0, Number(meta.total ?? meta.expectedItemCount) || 0);
  if (total <= 0) return;
  const map = readIngestMetaMap();
  map[bk] = {
    total,
    sourceLabel: String(meta.sourceLabel || map[bk]?.sourceLabel || '').trim() || undefined,
    createdAt: map[bk]?.createdAt || meta.createdAt || new Date().toISOString(),
  };
  writeIngestMetaMap(map);
}

export function readListingBatchIngestMeta(batchKey) {
  const bk = String(batchKey || '').trim();
  if (!bk) return null;
  const row = readIngestMetaMap()[bk];
  if (!row) return null;
  const total = Math.max(0, Number(row.total) || 0);
  if (total <= 0) return null;
  return {
    expectedItemCount: total,
    sourceLabel: row.sourceLabel || '',
    createdAt: row.createdAt || '',
  };
}

export function clearListingBatchIngestMeta(batchKey) {
  const bk = String(batchKey || '').trim();
  if (!bk) return;
  const map = readIngestMetaMap();
  if (!map[bk]) return;
  delete map[bk];
  writeIngestMetaMap(map);
}

function countItemsByBatchKey(items = []) {
  const counts = new Map();
  (Array.isArray(items) ? items : []).forEach((row) => {
    const bk = String(row?.batchKey || row?.batch_key || '').trim();
    if (!bk) return;
    counts.set(bk, (counts.get(bk) || 0) + 1);
  });
  return counts;
}

export const LISTING_INGEST_PROGRESS_EVENT = 'ipe-listing-ingest-progress';

export function emitListingIngestProgress(batchKey) {
  if (typeof window === 'undefined') return;
  const bk = String(batchKey || '').trim();
  window.dispatchEvent(new CustomEvent(LISTING_INGEST_PROGRESS_EVENT, { detail: { batchKey: bk } }));
}

/**
 * 是否存在需要轮询的入库任务。
 * 仅看 session 进行中占位；localStorage meta 只用于展示 x/total，不能作为轮询条件，
 * 否则历史批次会让定时器永不停止。
 */
export function hasActiveListingIngestWatch() {
  return listPendingListingIngestBatchKeys().length > 0;
}

/**
 * 记录进行中的本地商品库写入任务。
 * @param {string} batchKey
 * @param {{ mode?: 'ingest'|'promote', total?: number, sourceLabel?: string }} [meta]
 */
export function markListingIngestPending(batchKey, meta = {}) {
  const bk = String(batchKey || '').trim();
  if (!bk) return;
  const map = readPendingMap();
  const total = Math.max(0, Number(meta.total) || 0);
  map[bk] = {
    mode: meta.mode === 'promote' ? 'promote' : 'ingest',
    done: Number(meta.done) || 0,
    total,
    sourceLabel: String(meta.sourceLabel || '').trim() || (meta.mode === 'promote' ? '素材采集' : '快捷入库'),
    createdAt: map[bk]?.createdAt || new Date().toISOString(),
    provisional: Boolean(meta.provisional),
    preparing: Boolean(meta.preparing),
  };
  writePendingMap(map);
  if (!isProvisionalListingIngestBatchKey(bk) && total > 0) {
    rememberListingBatchIngestMeta(bk, {
      total,
      sourceLabel: map[bk].sourceLabel,
      createdAt: map[bk].createdAt,
    });
  }
  emitListingIngestProgress(bk);
}

export function updateListingIngestPendingProgress(batchKey, done, total) {
  const bk = String(batchKey || '').trim();
  if (!bk) return;
  const map = readPendingMap();
  const current = map[bk];
  if (!current) return;
  const parsedTotal = Number(total) > 0
    ? Math.max(0, Number(total) || 0)
    : Math.max(current.total || 0, Number(total) || 0);
  map[bk] = {
    ...current,
    done: Math.max(0, Number(done) || 0),
    total: parsedTotal,
  };
  if (!isProvisionalListingIngestBatchKey(bk) && parsedTotal > 0) {
    rememberListingBatchIngestMeta(bk, {
      total: parsedTotal,
      sourceLabel: map[bk].sourceLabel,
      createdAt: map[bk].createdAt,
    });
  }
  writePendingMap(map);
  emitListingIngestProgress(bk);
}

/** 富化分组完成后修正占位批次预期 SPU 数（避免按图片数误估） */
export function reviseListingIngestPendingTotal(batchKey, total) {
  const bk = String(batchKey || '').trim();
  if (!bk) return;
  const map = readPendingMap();
  const current = map[bk];
  if (!current) return;
  const parsedTotal = Math.max(0, Number(total) || 0);
  if (parsedTotal <= 0) return;
  map[bk] = {
    ...current,
    total: parsedTotal,
    done: Math.min(Number(current.done) || 0, parsedTotal),
  };
  if (!isProvisionalListingIngestBatchKey(bk)) {
    rememberListingBatchIngestMeta(bk, {
      total: parsedTotal,
      sourceLabel: map[bk].sourceLabel,
      createdAt: map[bk].createdAt,
    });
  }
  writePendingMap(map);
  emitListingIngestProgress(bk);
}

export function clearListingIngestPending(batchKey) {
  const bk = String(batchKey || '').trim();
  if (!bk) return;
  const map = readPendingMap();
  if (!map[bk]) return;
  delete map[bk];
  writePendingMap(map);
  emitListingIngestProgress(bk);
}

export function isProvisionalListingIngestBatchKey(batchKey) {
  return String(batchKey || '').trim().startsWith('__prep__');
}

/** 将临时占位批次迁移到服务端返回的真实 batchKey */
export function migrateListingIngestPending(fromKey, toKey, patch = {}) {
  const from = String(fromKey || '').trim();
  const to = String(toKey || '').trim();
  if (!from || !to || from === to) return;
  const map = readPendingMap();
  const current = map[from];
  if (!current) {
    markListingIngestPending(to, patch);
    return;
  }
  delete map[from];
  map[to] = {
    ...current,
    ...patch,
    provisional: false,
    preparing: false,
    createdAt: current.createdAt || new Date().toISOString(),
  };
  writePendingMap(map);
  if (!isProvisionalListingIngestBatchKey(from)) {
    clearListingBatchIngestMeta(from);
  }
  const total = Math.max(0, Number(map[to]?.total) || 0);
  if (!isProvisionalListingIngestBatchKey(to) && total > 0) {
    rememberListingBatchIngestMeta(to, {
      total,
      sourceLabel: map[to].sourceLabel,
      createdAt: map[to].createdAt,
    });
  }
  emitListingIngestProgress(to);
}

/** 同步读取 session 中的入库占位批次（不请求后端，用于列表即时展示） */
export function getLocalPendingListingIngestBatches(batchKeys = []) {
  const map = readPendingMap();
  const keys = [...new Set(
    (Array.isArray(batchKeys) && batchKeys.length > 0 ? batchKeys : Object.keys(map))
      .map((k) => String(k || '').trim())
      .filter(Boolean),
  )];
  return keys
    .map((batchKey) => {
      const pending = map[batchKey];
      if (!pending) return null;
      return pendingListingBatchFromLocalState(batchKey, pending);
    })
    .filter(Boolean);
}

/** 入库流程结束：保留批次占位直至列表已拉到对应商品 */
export function completeListingIngestPending(batchKey, total) {
  const bk = String(batchKey || '').trim();
  if (!bk) return;
  const map = readPendingMap();
  const current = map[bk];
  if (!current) return;
  const parsedTotal = Math.max(
    Number(total) || 0,
    Number(current.total) || 0,
    Number(current.done) || 0,
  );
  map[bk] = {
    ...current,
    done: parsedTotal,
    total: parsedTotal,
    completedAt: new Date().toISOString(),
  };
  if (!isProvisionalListingIngestBatchKey(bk) && parsedTotal > 0) {
    rememberListingBatchIngestMeta(bk, {
      total: parsedTotal,
      sourceLabel: map[bk].sourceLabel,
      createdAt: map[bk].createdAt,
    });
  }
  writePendingMap(map);
  emitListingIngestProgress(bk);
}

/**
 * 列表已出现该批次商品后，仅在入库流程全部完成时移除 session 占位。
 * @param {object[]} items normalize 后的 pending_listing 条目
 */
export function pruneListingIngestPendingForItems(items = []) {
  const rows = Array.isArray(items) ? items : [];
  const map = readPendingMap();
  const keys = Object.keys(map);
  if (keys.length === 0) return;
  const listCountByBatch = countItemsByBatchKey(rows);
  keys.forEach((key) => {
    if (isProvisionalListingIngestBatchKey(key)) return;
    const pending = map[key];
    if (!pending) return;
    const total = Number(pending.total) || 0;
    const done = Number(pending.done) || 0;
    const listCount = listCountByBatch.get(key) || 0;
    const ingestFinished = Boolean(pending.completedAt) || (total > 0 && done >= total);
    if (!ingestFinished) return;
    // 已 finalize 且列表已有商品：即使 expected 曾按图片数估大，也不要卡住轮询
    if (pending.completedAt && listCount > 0) {
      clearListingIngestPending(key);
      clearListingBatchIngestMeta(key);
      rememberListingBatchIngestMeta(key, { total: listCount, sourceLabel: pending.sourceLabel });
      return;
    }
    if (total > 0 && listCount < total) return;
    clearListingIngestPending(key);
    clearListingBatchIngestMeta(key);
  });
}

export function listPendingListingIngestBatchKeys() {
  return Object.keys(readPendingMap());
}

export function isListingIngestBatchActive(batch) {
  if (!batch) return false;
  if (batch.ingestPending) return true;
  const status = resolveEffectiveCaptureStatus(batch);
  return status === 'uploading' && shouldPollSourcingBatch(batch);
}

export function resolveListingIngestBatchProgress(batch) {
  const progress = resolveBatchCollectProgress(batch);
  const status = resolveEffectiveCaptureStatus(batch);
  const active = isListingIngestBatchActive(batch);
  return {
    ...progress,
    active,
    status,
    label: active ? (progress.label || '入库') : '',
  };
}

function resolveEffectiveListingIngestState({
  done = 0,
  total = 0,
  completedAt = '',
  ingestPending = false,
  itemCount = 0,
} = {}) {
  const pipelineDone = Math.max(Number(done) || 0, 0);
  const visibleCount = Math.max(Number(itemCount) || 0, 0);
  const parsedTotal = Math.max(Number(total) || 0, pipelineDone, visibleCount);
  const pipelineComplete = Boolean(completedAt) || (parsedTotal > 0 && pipelineDone >= parsedTotal);
  const fullySynced = parsedTotal <= 0 || visibleCount >= parsedTotal;
  const active = !fullySynced && (
    ingestPending
    || !pipelineComplete
    || (pipelineComplete && visibleCount < parsedTotal)
  );
  const displayDone = pipelineComplete && !fullySynced
    ? visibleCount
    : Math.max(pipelineDone, visibleCount);
  const percent = parsedTotal > 0
    ? Math.min(100, Math.round((displayDone / parsedTotal) * 100))
    : ((pipelineComplete && fullySynced) ? 100 : 0);
  return {
    active,
    done: displayDone,
    total: parsedTotal,
    percent,
    label: active ? '入库' : '',
    detail: parsedTotal > 0 ? `${displayDone}/${parsedTotal}` : `${displayDone}`,
    pipelineComplete,
    fullySynced,
  };
}

function isListingBatchIngestSettled(batch) {
  const status = resolveEffectiveCaptureStatus(batch);
  if (status === 'completed' || status === 'terminated') return true;
  if (Boolean(batch?.ingestFinalized ?? batch?.ingest_finalized)) return true;
  return false;
}

function resolveIngestExpectedTotal(batch, metaExpected = 0) {
  const spuCount = countListingBatchSpus(batch);
  const backendExpected = Number(batch?.expectedItemCount ?? 0) || 0;
  const meta = Math.max(0, Number(metaExpected) || 0);
  // 入库已完成：以实际 SPU 为准，丢弃错误的图片数预期（如 18 图 / 3 SPU）
  if (isListingBatchIngestSettled(batch) && spuCount > 0) {
    return spuCount;
  }
  // 富化后前端会 revise meta；取较小值避免图片预估盖住 SPU 数
  if (backendExpected > 0 && meta > 0) {
    return Math.min(backendExpected, meta);
  }
  return Math.max(backendExpected, meta, 0);
}

function applyPersistedIngestMeta(batch) {
  if (!batch) return batch;
  const key = String(batch.batchKey || '').trim();
  if (!key) return batch;
  const meta = readListingBatchIngestMeta(key);
  if (!meta?.expectedItemCount) return batch;
  const spuCount = countListingBatchSpus(batch);
  const total = resolveIngestExpectedTotal(batch, meta.expectedItemCount);
  if (total <= 0) return batch;
  // 纠正 localStorage 中偏大的图片数预期，避免刷新后又变 3/18
  if (meta.expectedItemCount > total) {
    rememberListingBatchIngestMeta(key, {
      total,
      sourceLabel: meta.sourceLabel,
      createdAt: meta.createdAt,
    });
  }
  const settled = isListingBatchIngestSettled(batch) && spuCount > 0;
  const ingestState = resolveEffectiveListingIngestState({
    done: Number(batch.ingestProgress?.done ?? spuCount) || spuCount,
    total,
    completedAt: settled
      ? (batch.ingestProgress?.completedAt || new Date().toISOString())
      : (batch.ingestProgress?.completedAt || ''),
    ingestPending: settled ? false : batch.ingestPending,
    itemCount: spuCount,
  });
  return {
    ...batch,
    expectedItemCount: total,
    sourceLabel: batch.sourceLabel || meta.sourceLabel || batch.sourceLabel,
    createdAt: batch.createdAt || meta.createdAt || batch.createdAt,
    ingestPending: settled ? false : ingestState.active,
    ingestProgress: {
      ...(batch.ingestProgress || {}),
      active: settled ? false : ingestState.active,
      done: settled ? spuCount : ingestState.done,
      total: settled ? spuCount : ingestState.total,
      percent: settled ? 100 : ingestState.percent,
      label: settled ? '' : ingestState.label,
      detail: settled ? String(spuCount) : ingestState.detail,
      completedAt: settled
        ? (batch.ingestProgress?.completedAt || new Date().toISOString())
        : (batch.ingestProgress?.completedAt || ''),
    },
    itemCount: settled ? spuCount : ingestState.done,
  };
}

function pendingListingBatchFromLocalState(batchKey, pending) {
  const done = Number(pending?.done) || 0;
  const total = Number(pending?.total) || 0;
  const ingestState = resolveEffectiveListingIngestState({
    done,
    total,
    completedAt: pending?.completedAt,
    ingestPending: total > 0 && done < total && !pending?.completedAt,
    itemCount: done,
  });
  const awaitingList = Boolean(pending?.completedAt) && total > 0 && done >= total;
  const active = ingestState.active || awaitingList;
  return {
    batchKey,
    items: [],
    itemCount: ingestState.done,
    expectedItemCount: ingestState.total > 0 ? ingestState.total : null,
    createdAt: pending?.createdAt || new Date().toISOString(),
    sourceLabel: pending?.sourceLabel || '快捷入库',
    previewImageUrls: [],
    ingestPending: active,
    ingestProgress: {
      active,
      done: ingestState.done,
      total: ingestState.total,
      percent: ingestState.percent,
      label: active ? '入库' : '',
      detail: ingestState.detail,
      completedAt: pending?.completedAt || '',
    },
  };
}

/**
 * 将 sourcing 批次元数据转为本地商品库列表可展示的占位批次。
 * @param {object} batch
 * @returns {object|null}
 */
export function pendingListingBatchFromCaptureBatch(batch) {
  const batchKey = String(batch?.batchKey || batch?.batch_key || '').trim();
  if (!batchKey) return null;
  const progress = resolveListingIngestBatchProgress(batch);
  const done = Number(batch?.itemCount ?? batch?.item_count ?? progress.done ?? 0) || 0;
  const total = Number(batch?.expectedItemCount ?? batch?.expected_item_count ?? progress.total ?? 0) || 0;
  return {
    batchKey,
    items: [],
    itemCount: done,
    expectedItemCount: total > 0 ? total : null,
    createdAt: batch?.createdAt || batch?.created_at || new Date().toISOString(),
    sourceLabel: '快捷入库',
    previewImageUrls: [],
    ingestPending: progress.active,
    ingestProgress: progress,
  };
}

/**
 * 合并已晋级商品批次与进行中的入库批次（按 batchKey 去重，已晋级优先）。
 * @param {object[]} itemBatches
 * @param {object[]} pendingBatches
 */
export function mergeListingBatchesWithPending(itemBatches = [], pendingBatches = []) {
  const map = new Map();
  (Array.isArray(itemBatches) ? itemBatches : []).forEach((batch) => {
    const key = String(batch?.batchKey || '').trim();
    if (!key) return;
    map.set(key, applyPersistedIngestMeta(batch));
  });
  (Array.isArray(pendingBatches) ? pendingBatches : []).forEach((pending) => {
    const key = String(pending?.batchKey || '').trim();
    if (!key) return;
    const existing = map.get(key);
    const progress = pending.ingestProgress || {};
    const spuCount = existing ? countListingBatchSpus(existing) : 0;
    const meta = readListingBatchIngestMeta(key);
    const done = Math.max(
      Number(progress.done ?? pending.itemCount ?? 0) || 0,
      spuCount,
    );
    const total = Number(
      pending.expectedItemCount
      ?? progress.total
      ?? existing?.expectedItemCount
      ?? meta?.expectedItemCount
      ?? 0,
    ) || 0;
    const ingestState = resolveEffectiveListingIngestState({
      done,
      total,
      completedAt: progress.completedAt || pending.completedAt,
      ingestPending: pending.ingestPending,
      itemCount: spuCount,
    });
    const merged = {
      ...(existing || pending),
      expectedItemCount: total > 0 ? total : (existing?.expectedItemCount ?? pending.expectedItemCount),
      ingestPending: ingestState.active,
      ingestProgress: {
        active: ingestState.active,
        done: ingestState.done,
        total: ingestState.total,
        percent: ingestState.percent,
        label: ingestState.label,
        detail: ingestState.detail,
        completedAt: progress.completedAt || pending.completedAt || '',
      },
      sourceLabel: pending.sourceLabel || existing?.sourceLabel || meta?.sourceLabel,
      itemCount: ingestState.done,
      items: existing?.items || pending.items || [],
      previewImageUrls: existing?.previewImageUrls?.length
        ? existing.previewImageUrls
        : (pending.previewImageUrls || []),
      createdAt: existing?.createdAt || pending.createdAt || meta?.createdAt,
    };
    map.set(key, applyPersistedIngestMeta(merged));
  });
  return [...map.values()].sort((a, b) => {
    const tb = new Date(b.createdAt || 0).getTime();
    const ta = new Date(a.createdAt || 0).getTime();
    if (Number.isFinite(tb) && Number.isFinite(ta) && tb !== ta) return tb - ta;
    return String(b.batchKey).localeCompare(String(a.batchKey));
  });
}

export const LISTING_BATCH_STATUS_LABELS = {
  ingesting: '入库中',
  uploading: '入库中',
  processing: '处理中',
  completed: '已完成',
  failed: '失败',
  partial_failed: '部分失败',
};

export function listingBatchStatusBadgeClass(status) {
  if (status === 'completed') return 'ipe-open-dock__badge--ok';
  if (status === 'failed') return 'ipe-open-dock__badge--danger';
  if (status === 'partial_failed' || status === 'processing' || status === 'ingesting' || status === 'uploading') {
    return 'ipe-open-dock__badge--warn';
  }
  return 'ipe-open-dock__badge--muted';
}

export function resolveListingBatchDisplay(batch) {
  if (!batch) {
    return {
      status: 'completed',
      statusLabel: '已完成',
      percent: 0,
      countText: '0',
    };
  }

  const spuCount = countListingBatchSpus(batch);
  // 采集已完成：数量按实际 SPU，不再用错误的 expected（图片数）维持「入库中」
  if (isListingBatchIngestSettled(batch) && spuCount > 0) {
    return {
      status: 'completed',
      statusLabel: LISTING_BATCH_STATUS_LABELS.completed,
      percent: 100,
      countText: String(spuCount),
    };
  }

  if (batch.ingestPending) {
    const progress = batch.ingestProgress || {};
    const done = Number(progress.done ?? batch.itemCount ?? 0) || 0;
    const total = Number(progress.total ?? batch.expectedItemCount ?? 0) || 0;
    const percent = Number.isFinite(Number(progress.percent))
      ? Math.min(100, Math.max(0, Math.round(Number(progress.percent))))
      : (total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0);
    return {
      status: 'ingesting',
      statusLabel: LISTING_BATCH_STATUS_LABELS.ingesting,
      percent,
      countText: total > 0 ? `${done}/${total}` : String(done),
    };
  }

  const expected = Number(batch.expectedItemCount ?? batch.ingestProgress?.total ?? 0) || 0;
  const progressDone = Number(batch.ingestProgress?.done ?? spuCount) || 0;
  if (expected > 0 && (spuCount < expected || progressDone < expected)) {
    const done = Math.max(spuCount, progressDone);
    const percent = Math.min(100, Math.round((done / expected) * 100));
    return {
      status: 'ingesting',
      statusLabel: LISTING_BATCH_STATUS_LABELS.ingesting,
      percent,
      countText: `${done}/${expected}`,
    };
  }

  const done = spuCount;
  const total = Number(batch.expectedItemCount ?? done) || done;
  return {
    status: 'completed',
    statusLabel: LISTING_BATCH_STATUS_LABELS.completed,
    percent: 100,
    countText: total > 0 && total !== done ? `${done}/${total}` : String(done),
  };
}

export function isListingBatchDisplayIngesting(batch) {
  return resolveListingBatchDisplay(batch).status === 'ingesting';
}

export function hasIngestingListingBatches(batches = []) {
  return (Array.isArray(batches) ? batches : []).some(isListingBatchDisplayIngesting);
}

/**
 * 列表展示为入库中但 session 占位已丢失时，补回进行中标记以恢复轮询。
 * @param {object[]} batches
 */
export function reconcileListingIngestPendingFromBatches(batches = []) {
  (Array.isArray(batches) ? batches : []).forEach((batch) => {
    const key = String(batch?.batchKey || '').trim();
    if (!key || isProvisionalListingIngestBatchKey(key)) return;
    if (!isListingBatchDisplayIngesting(batch)) return;

    const map = readPendingMap();
    const progress = batch.ingestProgress || {};
    const total = Math.max(
      0,
      Number(progress.total ?? batch.expectedItemCount ?? 0) || 0,
    );
    const done = Math.max(
      0,
      Number(progress.done ?? batch.itemCount ?? 0) || 0,
    );
    const sourceLabel = String(batch.sourceLabel || '').trim();

    if (map[key]) {
      const prevTotal = Math.max(0, Number(map[key].total) || 0);
      const prevDone = Math.max(0, Number(map[key].done) || 0);
      const nextTotal = Math.max(prevTotal, total);
      const nextDone = Math.max(prevDone, done);
      if (nextDone !== prevDone || nextTotal !== prevTotal) {
        updateListingIngestPendingProgress(key, nextDone, nextTotal > 0 ? nextTotal : prevTotal);
      }
      return;
    }

    markListingIngestPending(key, {
      mode: 'ingest',
      total: total > 0 ? total : undefined,
      done,
      sourceLabel: sourceLabel || undefined,
    });
  });
}

export async function fetchPendingListingIngestBatches(batchKeys = []) {
  const map = readPendingMap();
  const keys = [...new Set(
    (Array.isArray(batchKeys) && batchKeys.length > 0 ? batchKeys : Object.keys(map))
      .map((k) => String(k || '').trim())
      .filter(Boolean),
  )];
  if (keys.length === 0) return [];

  const results = [];
  for (const batchKey of keys) {
    const pending = map[batchKey];
    if (!pending) continue;

    if (pending.mode === 'promote' || pending.provisional || isProvisionalListingIngestBatchKey(batchKey)) {
      results.push(pendingListingBatchFromLocalState(batchKey, pending));
      continue;
    }

    try {
      const batch = await sourcingAPI.getBatch(batchKey);
      if (!batch?.batchKey) {
        results.push(pendingListingBatchFromLocalState(batchKey, pending));
        continue;
      }
      const row = pendingListingBatchFromCaptureBatch(batch);
      const local = pendingListingBatchFromLocalState(batchKey, pending);
      const spuCount = Math.max(
        countListingBatchSpus(row || {}),
        pending.done || 0,
        local.itemCount || 0,
      );
      const total = Number(
        pending.total || local.expectedItemCount || row?.expectedItemCount || 0,
      ) || 0;
      const ingestState = resolveEffectiveListingIngestState({
        done: Math.max(spuCount, pending.done || 0, local.itemCount || 0),
        total,
        completedAt: pending.completedAt,
        ingestPending: local.ingestPending,
        itemCount: spuCount,
      });
      results.push({
        ...(row || local),
        itemCount: ingestState.done,
        expectedItemCount: ingestState.total > 0 ? ingestState.total : null,
        ingestPending: ingestState.active,
        ingestProgress: {
          active: ingestState.active,
          done: ingestState.done,
          total: ingestState.total,
          percent: ingestState.percent,
          label: ingestState.label,
          detail: ingestState.detail,
        },
        sourceLabel: pending.sourceLabel || row?.sourceLabel || local.sourceLabel,
      });
    } catch {
      results.push(pendingListingBatchFromLocalState(batchKey, pending));
    }
  }
  return results;
}
